import { useCallback, useEffect, useRef, useState } from "react";

import type { CursorDirectoryPage, UseCursorDirectoryOptions, UseCursorDirectoryResult } from "./useCursorDirectory.types";

export function useCursorDirectory<T extends CursorDirectoryPage>({ load }: UseCursorDirectoryOptions<T>): UseCursorDirectoryResult<T> {
  const cursors = useRef<Array<string | null>>([null]);
  const sequence = useRef(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [knownPageCount, setKnownPageCount] = useState(1);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadPage = useCallback(async (page: number): Promise<T | null> => {
    const cursor = cursors.current[page];
    if (page < 0 || cursor === undefined) return null;
    const currentRequest = ++sequence.current;
    setIsLoading(true);
    setError(null);
    try {
      const value = await load(cursor ?? undefined);
      if (currentRequest !== sequence.current) return value;

      // Refreshing an earlier page invalidates later cursors because the underlying directory may have changed.
      const nextCursors = cursors.current.slice(0, page + 1);
      if (value.nextCursor) nextCursors.push(value.nextCursor);
      cursors.current = nextCursors;
      setKnownPageCount(nextCursors.length);
      setCurrentPage(page);
      setData(value);
      return value;
    } catch (reason) {
      if (currentRequest === sequence.current) {
        setError(reason instanceof Error ? reason.message : "Kayıt listesi yüklenemedi.");
      }
      return null;
    } finally {
      if (currentRequest === sequence.current) setIsLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void loadPage(0);
    return () => { sequence.current += 1; };
  }, [loadPage]);

  const refresh = useCallback(() => loadPage(currentPage), [currentPage, loadPage]);

  return {
    currentPage,
    data,
    error,
    hasNextPage: Boolean(data?.nextCursor),
    isLoading,
    knownPageCount,
    loadPage,
    refresh,
  };
}
